import {
  Component,
  For,
  JSXElement,
  Match,
  Show,
  Switch,
  createResource,
  createSignal,
  useContext,
} from 'solid-js';
import {
  AuthContext,
  AuthProvider,
  Button,
  CredentialProviderLabel,
  LocalizationContext,
  useRoutingNavigate,
} from '../components';

const AuthDialog: Component = () => {
  const { t } = useContext(LocalizationContext)!;
  const { providers, startAuth } = useContext(AuthContext)!;

  const [selectedProvider, setSelectedProvider] = createSignal<AuthProvider | null>(null);

  const onStartAuth = async (provider: AuthProvider) => {
    if(selectedProvider()) return;
    setSelectedProvider(provider);
    await startAuth(provider, 'register');
  };

  return <>
    <div class="sheet small ui auth">
      <h1>{ t('auth.dialog.header') }</h1>
      <Show when={selectedProvider() == null} fallback={
        <p>{ t('auth.dialog.redirecting_to')(t(`auth.provider.${selectedProvider()}`)) }</p>
      }>
        <p>{ t('auth.dialog.please_select') }</p>
        <div class="buttons">
          <For each={providers}>{
            (provider) =>
              <button class="button" onClick={[onStartAuth, provider]}><CredentialProviderLabel provider={provider} /></button>
          }</For>
        </div>
      </Show>
      <div class="footer"></div>
    </div>
  </>;
};

export const Oauth: Component<{
  route: string;
}> = (props) => {
  const { t } = useContext(LocalizationContext)!;
  const { finishAuth } = useContext(AuthContext)!;
  const provider = props.route as AuthProvider;
  const navigate = useRoutingNavigate();

  const [authError] = createResource(async () => {
    const { intent, nextUrl, error } = await finishAuth(provider);
    let actualNextUrl = nextUrl ?? undefined;
    if(error || !nextUrl) {
      switch(intent) {
      case 'login':
      case 'register':
        actualNextUrl = '/';
        break;
      case 'link':
        actualNextUrl = '/me';
        break;
      }
    }
    if(error || !actualNextUrl) {
      return {
        nextUrl: actualNextUrl ?? '/',
        error,
      };
    } else {
      navigate(actualNextUrl, {
        replace: true,
        reload: true,
      });
    }
  });

  return <>
    <Switch fallback={
      <ProgressSheet title={t('auth.dialog.progress_header')} />
    }>
      <Match when={authError()}>{(authError) =>
        <ErrorSheet
          title={t('auth.dialog.error.header')}
          message={t(`auth.dialog.error.${authError().error}`) ?? t('auth.dialog.error.unknown')}
          links={[{
            title: t('auth.dialog.error.back'),
            link: authError().nextUrl,
          }]}
        />
      }</Match>
    </Switch>
  </>;
};

export const AuthWall: Component = () => {
  const { t } = useContext(LocalizationContext)!;
  const { status } = useContext(AuthContext)!;

  return (
    <Switch>
      <Match when={({
        not_authenticated: true,
      } as {
        [k: string]: boolean;
      })[status()]}>
        <AuthDialog />
      </Match>
      <Match when={status() == 'error'}>
        <div>{t('error')}</div>
      </Match>
      <Match when={true}>
        <ProgressSheet title={t('auth.dialog.progress_header')} />
      </Match>
    </Switch>
  );
};

const ProgressSheet: Component<{
  title: JSXElement;
  children?: JSXElement;
}> = (props) => {
  return (
    <div class="sheet ui small auth_progress">
      <h1>{ props.title }</h1>
      { props.children }
      <div class="footer"></div>
    </div>
  );
};

const ErrorSheet: Component<{
  title: JSXElement;
  message: JSXElement;
  links?: {
    title: string;
    link: string;
  }[];
}> = (props) => {
  const navigate = useRoutingNavigate();
  return (
    <div class="sheet ui small error">
      <h1>{ props.title }</h1>
      <div class="message">{props.message}</div>
      <div class="footer">
        <div class="buttons align_end">
          <For each={props.links}>{({title, link}) =>
            <Button onClick={() => navigate(link, {
              replace: true,
              reload: true,
            })}>{title}</Button>
          }</For>
        </div>
      </div>
    </div>
  );
};
